/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ApriorMapRead } from '../models/ApriorMapRead';
import type { ApriorMapVersionRead } from '../models/ApriorMapVersionRead';
import type { CleaningZone } from '../models/CleaningZone';
import type { CleaningZoneOrderingRequest } from '../models/CleaningZoneOrderingRequest';
import type { CleaningZoneRead } from '../models/CleaningZoneRead';
import type { CleaningZoneRequest } from '../models/CleaningZoneRequest';
import type { DiagnosticLog } from '../models/DiagnosticLog';
import type { Mission } from '../models/Mission';
import type { MissionHistory } from '../models/MissionHistory';
import type { MissionJob } from '../models/MissionJob';
import type { MissionJobRead } from '../models/MissionJobRead';
import type { MissionJobRequest } from '../models/MissionJobRequest';
import type { MissionJobRobotRequest } from '../models/MissionJobRobotRequest';
import type { MissionPointTransferRoute } from '../models/MissionPointTransferRoute';
import type { MissionPointTransferRouteRead } from '../models/MissionPointTransferRouteRead';
import type { MissionPointTransferRouteRequest } from '../models/MissionPointTransferRouteRequest';
import type { MissionRead } from '../models/MissionRead';
import type { MissionRequest } from '../models/MissionRequest';
import type { MissionStatus } from '../models/MissionStatus';
import type { PaginatedApriorMapReadList } from '../models/PaginatedApriorMapReadList';
import type { PaginatedApriorMapVersionReadList } from '../models/PaginatedApriorMapVersionReadList';
import type { PaginatedDiagnosticLogList } from '../models/PaginatedDiagnosticLogList';
import type { PaginatedMissionHistoryList } from '../models/PaginatedMissionHistoryList';
import type { PaginatedMissionJobReadList } from '../models/PaginatedMissionJobReadList';
import type { PaginatedMissionPointTransferRouteReadList } from '../models/PaginatedMissionPointTransferRouteReadList';
import type { PaginatedMissionReadListList } from '../models/PaginatedMissionReadListList';
import type { PaginatedRestrictZoneReadList } from '../models/PaginatedRestrictZoneReadList';
import type { PaginatedRobotListList } from '../models/PaginatedRobotListList';
import type { PaginatedTransferRouteReadList } from '../models/PaginatedTransferRouteReadList';
import type { PatchedCleaningZoneRequest } from '../models/PatchedCleaningZoneRequest';
import type { PatchedMissionPointTransferRouteRequest } from '../models/PatchedMissionPointTransferRouteRequest';
import type { PatchedMissionRequest } from '../models/PatchedMissionRequest';
import type { PatchedRestrictZoneRequest } from '../models/PatchedRestrictZoneRequest';
import type { PatchedRobotRequest } from '../models/PatchedRobotRequest';
import type { PatchedTransferRouteRequest } from '../models/PatchedTransferRouteRequest';
import type { RestrictZone } from '../models/RestrictZone';
import type { RestrictZoneRead } from '../models/RestrictZoneRead';
import type { RestrictZoneRequest } from '../models/RestrictZoneRequest';
import type { Robot } from '../models/Robot';
import type { RobotRead } from '../models/RobotRead';
import type { RobotRequest } from '../models/RobotRequest';
import type { TransferRoute } from '../models/TransferRoute';
import type { TransferRouteRead } from '../models/TransferRouteRead';
import type { TransferRouteRequest } from '../models/TransferRouteRequest';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class MainService {

    /**
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedApriorMapReadList
     * @throws ApiError
     */
    public static mainApriorMapList(
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedApriorMapReadList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map/',
            query: {
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedApriorMapVersionReadList
     * @throws ApiError
     */
    public static mainApriorMapVersionList(
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedApriorMapVersionReadList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map-version/',
            query: {
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this aprior map version.
     * @returns ApriorMapVersionRead
     * @throws ApiError
     */
    public static mainApriorMapVersionRetrieve(
        id: number,
    ): CancelablePromise<ApriorMapVersionRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map-version/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param versionUuid
     * @returns binary
     * @throws ApiError
     */
    public static mainApriorMapVersionOvrRetrieve(
        versionUuid: string,
    ): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map-version/{version_uuid}/ovr/',
            path: {
                'version_uuid': versionUuid,
            },
        });
    }

    /**
     * @param versionUuid
     * @returns binary
     * @throws ApiError
     */
    public static mainApriorMapVersionTifRetrieve(
        versionUuid: string,
    ): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map-version/{version_uuid}/tif/',
            path: {
                'version_uuid': versionUuid,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this aprior map.
     * @returns ApriorMapRead
     * @throws ApiError
     */
    public static mainApriorMapRetrieve(
        id: number,
    ): CancelablePromise<ApriorMapRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param geometry GeoJSON geometry string
     * @param inBbox BBox format: min_lon,min_lat,max_lon,max_lat
     * @returns ApriorMapRead
     * @throws ApiError
     */
    public static mainApriorMapRegionList(
        geometry?: string,
        inBbox?: string,
    ): CancelablePromise<Array<ApriorMapRead>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/aprior-map/region/',
            query: {
                'geometry': geometry,
                'in_bbox': inBbox,
            },
        });
    }

    /**
     * @param missionId
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedMissionReadListList
     * @throws ApiError
     */
    public static mainCleaningZoneList(
        missionId?: Array<number>,
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedMissionReadListList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/cleaning-zone/',
            query: {
                'mission_id': missionId,
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param requestBody
     * @returns CleaningZone
     * @throws ApiError
     */
    public static mainCleaningZoneCreate(
        requestBody: CleaningZoneRequest,
    ): CancelablePromise<CleaningZone> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/cleaning-zone/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this cleaning zone.
     * @returns CleaningZoneRead
     * @throws ApiError
     */
    public static mainCleaningZoneRetrieve(
        id: number,
    ): CancelablePromise<CleaningZoneRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/cleaning-zone/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this cleaning zone.
     * @param requestBody
     * @returns CleaningZone
     * @throws ApiError
     */
    public static mainCleaningZonePartialUpdate(
        id: number,
        requestBody?: PatchedCleaningZoneRequest,
    ): CancelablePromise<CleaningZone> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/cleaning-zone/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this cleaning zone.
     * @returns void
     * @throws ApiError
     */
    public static mainCleaningZoneDestroy(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/main/cleaning-zone/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param requestBody
     * @returns MissionRead
     * @throws ApiError
     */
    public static mainCleaningZoneOrderingPartialUpdate(
        requestBody: Array<CleaningZoneOrderingRequest>,
    ): CancelablePromise<MissionRead> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/cleaning-zone/ordering/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param robot
     * @returns PaginatedDiagnosticLogList
     * @throws ApiError
     */
    public static mainDiagnosticLogList(
        page?: number,
        pageSize?: number,
        robot?: number,
    ): CancelablePromise<PaginatedDiagnosticLogList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/diagnostic-log/',
            query: {
                'page': page,
                'page_size': pageSize,
                'robot': robot,
            },
        });
    }

    /**
     * @param id A UUID string identifying this diagnostic log.
     * @returns DiagnosticLog
     * @throws ApiError
     */
    public static mainDiagnosticLogRetrieve(
        id: string,
    ): CancelablePromise<DiagnosticLog> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/diagnostic-log/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Скачивание файла миссии (JobFile) роботом по short-lived токену (внешний контур: роботы).
     *
     * Файл отдаёт nginx через X-Accel-Redirect, минуя app-процесс.
     * @param missionId
     * @returns binary
     * @throws ApiError
     */
    public static mainDownloadMissionRetrieve(
        missionId: number,
    ): CancelablePromise<Blob> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/download-mission/{mission_id}',
            path: {
                'mission_id': missionId,
            },
        });
    }

    /**
     * @param q Query parameter for geocoding
     * @returns any No response body
     * @throws ApiError
     */
    public static mainGeocodeRetrieve(
        q: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/geocode/',
            query: {
                'q': q,
            },
        });
    }

    /**
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedMissionReadListList
     * @throws ApiError
     */
    public static mainMissionList(
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedMissionReadListList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission/',
            query: {
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param requestBody
     * @returns Mission
     * @throws ApiError
     */
    public static mainMissionCreate(
        requestBody?: MissionRequest,
    ): CancelablePromise<Mission> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param mission
     * @param name
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param robot
     * @returns PaginatedMissionHistoryList
     * @throws ApiError
     */
    public static mainMissionHistoryList(
        mission?: number,
        name?: string,
        page?: number,
        pageSize?: number,
        robot?: number,
    ): CancelablePromise<PaginatedMissionHistoryList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission-history/',
            query: {
                'mission': mission,
                'name': name,
                'page': page,
                'page_size': pageSize,
                'robot': robot,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission history.
     * @returns MissionHistory
     * @throws ApiError
     */
    public static mainMissionHistoryRetrieve(
        id: number,
    ): CancelablePromise<MissionHistory> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission-history/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param missionId
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param robotId
     * @param search A search term.
     * @param status
     * @returns PaginatedMissionJobReadList
     * @throws ApiError
     */
    public static mainMissionJobList(
        missionId?: Array<number>,
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        robotId?: Array<string | null>,
        search?: string,
        status?: 'new' | 'scenario_calculating' | 'scenario_done' | 'scenario_error',
    ): CancelablePromise<PaginatedMissionJobReadList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission-job/',
            query: {
                'mission_id': missionId,
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'robot_id': robotId,
                'search': search,
                'status': status,
            },
        });
    }

    /**
     * @param requestBody
     * @returns MissionJob
     * @throws ApiError
     */
    public static mainMissionJobCreate(
        requestBody: MissionJobRequest,
    ): CancelablePromise<MissionJob> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission-job/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this mission job.
     * @returns MissionJobRead
     * @throws ApiError
     */
    public static mainMissionJobRetrieve(
        id: number,
    ): CancelablePromise<MissionJobRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission-job/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission job.
     * @returns any No response body
     * @throws ApiError
     */
    public static mainMissionJobStartCalculationCreate(
        id: number,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission-job/{id}/start-calculation/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param requestBody
     * @returns any No response body
     * @throws ApiError
     */
    public static mainMissionJobClearMissionByRobotCreate(
        requestBody: MissionJobRobotRequest,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission-job/clear-mission-by-robot/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param cleaningZone
     * @param missionId
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedMissionPointTransferRouteReadList
     * @throws ApiError
     */
    public static mainMissionPointTransferRouteList(
        cleaningZone?: Array<number>,
        missionId?: Array<number>,
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedMissionPointTransferRouteReadList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission-point-transfer-route/',
            query: {
                'cleaning_zone': cleaningZone,
                'mission_id': missionId,
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param requestBody
     * @returns MissionPointTransferRoute
     * @throws ApiError
     */
    public static mainMissionPointTransferRouteCreate(
        requestBody: MissionPointTransferRouteRequest,
    ): CancelablePromise<MissionPointTransferRoute> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission-point-transfer-route/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this mission point transfer route.
     * @returns MissionPointTransferRouteRead
     * @throws ApiError
     */
    public static mainMissionPointTransferRouteRetrieve(
        id: number,
    ): CancelablePromise<MissionPointTransferRouteRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission-point-transfer-route/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission point transfer route.
     * @param requestBody
     * @returns MissionPointTransferRoute
     * @throws ApiError
     */
    public static mainMissionPointTransferRoutePartialUpdate(
        id: number,
        requestBody?: PatchedMissionPointTransferRouteRequest,
    ): CancelablePromise<MissionPointTransferRoute> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/mission-point-transfer-route/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this mission point transfer route.
     * @returns void
     * @throws ApiError
     */
    public static mainMissionPointTransferRouteDestroy(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/main/mission-point-transfer-route/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission.
     * @returns MissionRead
     * @throws ApiError
     */
    public static mainMissionRetrieve(
        id: number,
    ): CancelablePromise<MissionRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission.
     * @param requestBody
     * @returns Mission
     * @throws ApiError
     */
    public static mainMissionPartialUpdate(
        id: number,
        requestBody?: PatchedMissionRequest,
    ): CancelablePromise<Mission> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/mission/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this mission.
     * @returns void
     * @throws ApiError
     */
    public static mainMissionDestroy(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/main/mission/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission.
     * @returns MissionRead
     * @throws ApiError
     */
    public static mainMissionDuplicateCreate(
        id: number,
    ): CancelablePromise<MissionRead> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission/{id}/duplicate/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Полное копирование миссии вместе с вычислительными артефактами
     * (cleaning route, transfer routes, mission point transfer routes).
     *
     * MissionHistory и JobFile копируются, ROS-запросы/ответы
     * (ros_request/ros_response/ros_error) не копируются,
     * статусы и вычислительные поля переносятся как есть.
     * Копия получает статус как у оригинала.
     * @param id A unique integer value identifying this mission.
     * @returns MissionRead
     * @throws ApiError
     */
    public static mainMissionDuplicateFullCreate(
        id: number,
    ): CancelablePromise<MissionRead> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission/{id}/duplicate-full/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this mission.
     * @returns MissionStatus
     * @throws ApiError
     */
    public static mainMissionGetStatusRetrieve(
        id: number,
    ): CancelablePromise<MissionStatus> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/mission/{id}/get-status/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * Чтобы принудительно перерасчитать маршруты для всего надо дослать `?force=1`
     * @param id A unique integer value identifying this mission.
     * @returns any No response body
     * @throws ApiError
     */
    public static mainMissionStartCalculationCreate(
        id: number,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/mission/{id}/start-calculation/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param missionId
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedRestrictZoneReadList
     * @throws ApiError
     */
    public static mainRestrictZoneList(
        missionId?: Array<number>,
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedRestrictZoneReadList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/restrict-zone/',
            query: {
                'mission_id': missionId,
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param requestBody
     * @returns RestrictZone
     * @throws ApiError
     */
    public static mainRestrictZoneCreate(
        requestBody: RestrictZoneRequest,
    ): CancelablePromise<RestrictZone> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/restrict-zone/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this restrict zone.
     * @returns RestrictZoneRead
     * @throws ApiError
     */
    public static mainRestrictZoneRetrieve(
        id: number,
    ): CancelablePromise<RestrictZoneRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/restrict-zone/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this restrict zone.
     * @param requestBody
     * @returns RestrictZone
     * @throws ApiError
     */
    public static mainRestrictZonePartialUpdate(
        id: number,
        requestBody?: PatchedRestrictZoneRequest,
    ): CancelablePromise<RestrictZone> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/restrict-zone/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this restrict zone.
     * @returns void
     * @throws ApiError
     */
    public static mainRestrictZoneDestroy(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/main/restrict-zone/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedRobotListList
     * @throws ApiError
     */
    public static mainRobotList(
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedRobotListList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/robot/',
            query: {
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param requestBody
     * @returns Robot
     * @throws ApiError
     */
    public static mainRobotCreate(
        requestBody?: RobotRequest,
    ): CancelablePromise<Robot> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/robot/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this robot.
     * @returns RobotRead
     * @throws ApiError
     */
    public static mainRobotRetrieve(
        id: number,
    ): CancelablePromise<RobotRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/robot/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this robot.
     * @param requestBody
     * @returns Robot
     * @throws ApiError
     */
    public static mainRobotPartialUpdate(
        id: number,
        requestBody?: PatchedRobotRequest,
    ): CancelablePromise<Robot> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/robot/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this robot.
     * @returns void
     * @throws ApiError
     */
    public static mainRobotDestroy(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/main/robot/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param cmd
     * @param id A unique integer value identifying this robot.
     * @returns any No response body
     * @throws ApiError
     */
    public static mainRobotExecCommandCreate(
        cmd: string,
        id: number,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/robot/{id}/exec-command/{cmd}/',
            path: {
                'cmd': cmd,
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this robot.
     * @param mode
     * @returns any No response body
     * @throws ApiError
     */
    public static mainRobotSwitchModeCreate(
        id: number,
        mode: string,
    ): CancelablePromise<any> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/robot/{id}/switch-mode/{mode}/',
            path: {
                'id': id,
                'mode': mode,
            },
        });
    }

    /**
     * @param cleaningZoneFromId
     * @param cleaningZoneToId
     * @param missionId
     * @param ordering Which field to use when ordering the results.
     * @param page A page number within the paginated result set.
     * @param pageSize Number of results to return per page.
     * @param search A search term.
     * @returns PaginatedTransferRouteReadList
     * @throws ApiError
     */
    public static mainTransferRouteList(
        cleaningZoneFromId?: Array<number>,
        cleaningZoneToId?: Array<number>,
        missionId?: Array<string>,
        ordering?: Array<'id' | '-id' | 'name' | '-name' | 'created_at' | '-created_at' | 'updated_at' | '-updated_at'>,
        page?: number,
        pageSize?: number,
        search?: string,
    ): CancelablePromise<PaginatedTransferRouteReadList> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/transfer-route/',
            query: {
                'cleaning_zone_from_id': cleaningZoneFromId,
                'cleaning_zone_to_id': cleaningZoneToId,
                'mission_id': missionId,
                'ordering': ordering,
                'page': page,
                'page_size': pageSize,
                'search': search,
            },
        });
    }

    /**
     * @param requestBody
     * @returns TransferRoute
     * @throws ApiError
     */
    public static mainTransferRouteCreate(
        requestBody?: TransferRouteRequest,
    ): CancelablePromise<TransferRoute> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/api/main/transfer-route/',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this transfer route.
     * @returns TransferRouteRead
     * @throws ApiError
     */
    public static mainTransferRouteRetrieve(
        id: number,
    ): CancelablePromise<TransferRouteRead> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/api/main/transfer-route/{id}/',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @param id A unique integer value identifying this transfer route.
     * @param requestBody
     * @returns TransferRoute
     * @throws ApiError
     */
    public static mainTransferRoutePartialUpdate(
        id: number,
        requestBody?: PatchedTransferRouteRequest,
    ): CancelablePromise<TransferRoute> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/api/main/transfer-route/{id}/',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @param id A unique integer value identifying this transfer route.
     * @returns void
     * @throws ApiError
     */
    public static mainTransferRouteDestroy(
        id: number,
    ): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/api/main/transfer-route/{id}/',
            path: {
                'id': id,
            },
        });
    }

}
